import {
	closestCenter,
	DndContext,
	DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors
} from "@dnd-kit/core";
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
	faAngleDown,
	faCheckCircle,
	faExclamationTriangle,
	faGripVertical,
	faPlus,
	faSitemap,
	faTimes,
	faTrash
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery } from "@tanstack/react-query";
import { JSX, useCallback, useEffect, useState } from "react";
import { apiClient } from "@/api/axiosInstance";
import { API_ENDPOINTS, resolveEndpoint } from "@/api/endpoints";
import { useAuthStore } from "@/store/auth.store";
import { useEnvironmentStore } from "@/store/environments.store";
import { FlowAction } from "@/store/flowActions.store";
import { FlowType } from "@/store/flowTypes.store";
import { Action, Condition, IRule, RuleSet } from "@/store/rule.store";
import { ApiError, handleApiError } from "@/utils/errorHandler";
import { getFlows } from "../Integrations/ViewFlow";
import { ruleApi } from "./index";

const generateTempId = () =>
	`temp-id-${Math.random().toString(36).substr(2, 9)}`;

const numberOperators = [
	{ value: "equals", label: "Equals" },
	{ value: "gte", label: "Greater than or equal" },
	{ value: "lte", label: "Less than or equal" },
	{ value: "gt", label: "Greater than" },
	{ value: "lt", label: "Less than" }
];

const stringOperators = [
	{ value: "equals", label: "Equals" },
	{ value: "contains", label: "Contains" },
	{ value: "startsWith", label: "Starts with" },
	{ value: "endsWith", label: "Ends with" }
];

const operandTypes = {
	"order.total": "number",
	"user.country": "string"
};

const getOperatorsForOperand = (operand: string) => {
	const type = operandTypes[operand as keyof typeof operandTypes] || "string";
	return type === "number" ? numberOperators : stringOperators;
};

const operands = [
	{ value: "order.total", label: "Order Total", type: "number" },
	{ value: "user.country", label: "User Country", type: "string" }
];

interface AddRuleFormProps {
	isEditing: boolean;
	ruleId?: string;
	onClose: () => void;
	onSubmit: (rule: IRule) => void;
	authHeaders: Record<string, string>;
}

interface SortableRuleSetProps {
	ruleSet: RuleSet;
	index: number;
	totalRulesets: number;
	addCondition: (ruleSetId: string) => void;
	addAction: (ruleSetId: string) => void;
	handleDeleteRuleSet: (ruleSetId: string) => void;
	renderEmptyState: (title: string, subtitle: string) => JSX.Element;
	renderCondition: (
		ruleSet: RuleSet,
		condition: Condition,
		index: number
	) => JSX.Element;
	renderAction: (
		ruleSet: RuleSet,
		action: Action,
		index: number
	) => JSX.Element;
}

const SortableRuleSet: React.FC<SortableRuleSetProps> = ({
	ruleSet,
	index,
	totalRulesets,
	addCondition,
	addAction,
	handleDeleteRuleSet,
	renderEmptyState,
	renderCondition,
	renderAction
}: SortableRuleSetProps) => {
	const { attributes, listeners, setNodeRef, transform, transition } =
		useSortable({
			id: ruleSet.id
		});

	const style = {
		transform: CSS.Transform?.toString(transform),
		transition
	};

	const showDragHandle = totalRulesets > 1;

	return (
		<div
			ref={setNodeRef}
			style={style}
			className="border border-gray-200 rounded-lg p-4 mb-4 bg-white"
		>
			<div className="flex items-center justify-between mb-4">
				<div className="flex items-center">
					{showDragHandle && (
						<div
							{...attributes}
							{...listeners}
							className="mr-2 px-2 py-1 text-gray-500 cursor-move hover:bg-gray-100 rounded"
							title="Drag to reorder"
						>
							<FontAwesomeIcon icon={faGripVertical} />
						</div>
					)}
					<span className="text-blue-600 font-medium">
						Rule Set {index + 1} (Priority: {ruleSet.order})
					</span>
				</div>
				<button
					type="button"
					onClick={() => handleDeleteRuleSet(ruleSet.id)}
					className="text-red-500 hover:text-red-700"
				>
					<FontAwesomeIcon icon={faTrash} />
				</button>
			</div>

			{/* Conditions */}
			<div className="mb-6">
				<div className="flex items-center justify-between mb-3">
					<h4 className="font-medium">
						Conditions ({ruleSet.conditions.length}){" "}
						<span className="text-red-500">*</span>
					</h4>
					<button
						type="button"
						onClick={() => addCondition(ruleSet.id)}
						className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
					>
						<FontAwesomeIcon icon={faPlus} />
						Add Condition
					</button>
				</div>

				{ruleSet.conditions.length === 0
					? renderEmptyState(
							"No conditions added yet",
							"Add at least one condition to continue"
						)
					: ruleSet.conditions.map((condition, index) =>
							renderCondition(ruleSet, condition, index)
						)}
			</div>

			{/* Execution */}
			<div>
				<div className="flex items-center justify-between mb-3">
					<h4 className="font-medium">
						Execution ({ruleSet.actions.length}){" "}
						<span className="text-red-500">*</span>
					</h4>
					<button
						type="button"
						onClick={() => addAction(ruleSet.id)}
						disabled={ruleSet.actions.length >= 1}
						className={`flex items-center gap-1 ${
							ruleSet.actions.length >= 1
								? "text-gray-400 cursor-not-allowed"
								: "text-green-600 hover:text-green-800"
						}`}
					>
						<FontAwesomeIcon icon={faPlus} />
						Add Execution
					</button>
				</div>

				{ruleSet.actions.length === 0
					? renderEmptyState(
							"No execution added yet",
							"Add at least one execution to continue"
						)
					: ruleSet.actions.map((action, index) =>
							renderAction(ruleSet, action, index)
						)}
			</div>
		</div>
	);
};

const AddRuleForm: React.FC<AddRuleFormProps> = ({
	isEditing,
	ruleId,
	onClose,
	onSubmit,
	authHeaders
}) => {
	const { brandId } = useAuthStore();
	const { selectedEnvironmentSecret, selectedEnvironmentId } =
		useEnvironmentStore();

	const [state, setState] = useState({
		error: null as string | null,
		rule: { flowActionId: "", ruleset: [] } as IRule,
		deleteModal: { show: false, ruleSetId: null as string | null },
		selectedFlowTypeId: "",
		selectedFlowTypeName: "",
		selectedFlowActionId: "",
		selectedFlowActionName: "",
		flows: {} as Record<
			string,
			{ id: string; name: string; targetName: string }[]
		>
	});

	const RULE_FLOW_TYPES_QUERY_KEY = [
		`brand-rule-flow-types-${brandId}-${selectedEnvironmentId}`
	];
	const RULE_FLOW_ACTIONS_QUERY_KEY = [
		`brand-rule-flow-actions-${brandId}-${selectedEnvironmentId}`
	];

	const flowTypesQuery = useQuery({
		queryKey: RULE_FLOW_TYPES_QUERY_KEY,
		queryFn: async () => {
			const { data } = await apiClient.get(
				resolveEndpoint(API_ENDPOINTS, ["FLOW_TYPES", "LIST"])
			);
			return data;
		}
	});

	const flowActionsQuery = useQuery({
		queryKey: [...RULE_FLOW_ACTIONS_QUERY_KEY, state.selectedFlowTypeId],
		queryFn: async () => {
			if (!state.selectedFlowTypeId) return [];
			const { data } = await apiClient.get(
				resolveEndpoint(
					API_ENDPOINTS,
					["FLOW_ACTIONS", "LIST"],
					state.selectedFlowTypeId
				)
			);
			return data;
		},
		enabled: !!state.selectedFlowTypeId
	});

	const updateState = useCallback((updates: Partial<typeof state>) => {
		setState((prev) => ({ ...prev, ...updates }));
	}, []);

	const updateRule = useCallback((updates: Partial<IRule>) => {
		setState((prev) => ({
			...prev,
			rule: { ...prev.rule, ...updates }
		}));
	}, []);

	const updateRuleset = useCallback(
		(updater: (ruleset: RuleSet[]) => RuleSet[]) => {
			setState((prev) => ({
				...prev,
				rule: {
					...prev.rule,
					ruleset: updater(prev.rule.ruleset)
				}
			}));
		},
		[]
	);

	useEffect(() => {
		if (state.selectedFlowTypeId && flowTypesQuery.data) {
			const selectedType = flowTypesQuery.data.find(
				(type: FlowType) => type.id === state.selectedFlowTypeId
			);
			updateState({
				selectedFlowActionId: "",
				selectedFlowTypeName: selectedType?.name || ""
			});
		}
	}, [state.selectedFlowTypeId, flowTypesQuery.data, updateState]);

	useEffect(() => {
		if (state.selectedFlowActionId && flowActionsQuery.data) {
			const selectedAction = flowActionsQuery.data.find(
				(action: FlowAction) => action.id === state.selectedFlowActionId
			);
			if (selectedAction) {
				updateRule({ flowActionId: state.selectedFlowActionId });
			}
		}
	}, [state.selectedFlowActionId, flowActionsQuery.data, updateRule]);

	useEffect(() => {
		const isDropdownDisabled =
			!state.selectedFlowTypeId ||
			!state.selectedFlowActionId ||
			flowActionsQuery.isLoading;
		if (isDropdownDisabled && !isEditing) {
			updateRuleset((ruleset) =>
				ruleset.map((ruleSet) => ({
					...ruleSet,
					actions: ruleSet.actions.some((action) => action.flowId)
						? ruleSet.actions.map((action) => ({ ...action, flowId: "" }))
						: ruleSet.actions
				}))
			);
		}
	}, [
		state.selectedFlowTypeId,
		state.selectedFlowActionId,
		flowActionsQuery.isLoading,
		isEditing,
		updateRuleset
	]);

	useEffect(() => {
		const fetchFlows = async () => {
			if (!selectedEnvironmentSecret || !state.selectedFlowTypeName) return;

			try {
				updateState({ error: null });
				const data = await getFlows({
					brandId: brandId as string,
					environmentId: selectedEnvironmentSecret as string,
					type: state.selectedFlowTypeName
				});

				const groupedFlows: Record<
					string,
					{ id: string; name: string; targetName: string }[]
				> = {};

				if (Array.isArray(data)) {
					data.forEach((item) => {
						item.targets?.forEach((target) => {
							target.actions?.forEach((action) => {
								if (action.definition?.flow) {
									const actionId = action.id;
									if (!groupedFlows[actionId]) groupedFlows[actionId] = [];
									groupedFlows[actionId].push({
										id: action.definition.flow.id,
										name: action.definition.description,
										targetName: target.name
									});
								}
							});
						});
					});
				}

				updateState({ flows: groupedFlows });
			} catch (error) {
				handleApiError(error as ApiError, "Failed to load flows");
				onClose();
			}
		};

		fetchFlows();
	}, [
		brandId,
		selectedEnvironmentSecret,
		state.selectedFlowTypeName,
		onClose,
		updateState
	]);

	useEffect(() => {
		if (isEditing && ruleId) {
			updateState({ error: null });
			ruleApi
				.getRuleById(ruleId, authHeaders)
				.then((data: IRule) => {
					updateRule(data);

					updateState({
						selectedFlowTypeId: data.flowTypeId || "",
						selectedFlowActionId: data.flowActionId || "",
						selectedFlowTypeName: data.flowType?.name || "",
						selectedFlowActionName: data.flowAction?.name || ""
					});
				})
				.catch((err: ApiError) => {
					updateState({ error: "Failed to load rule data. Please try again." });
					handleApiError(err, "Failed to load rule data. Please try again.");
				});
		}
	}, [isEditing, ruleId, authHeaders, updateState, updateRule]);

	const addRuleSet = () => {
		const newRuleSet: RuleSet = {
			id: generateTempId(),
			order: state.rule.ruleset.length
				? Math.max(...state.rule.ruleset.map((rs) => rs.order)) + 1
				: 1,
			conditions: [],
			actions: []
		};
		updateRuleset((ruleset) => [...ruleset, newRuleSet]);
	};

	const removeRuleSet = (ruleSetId: string) => {
		updateRuleset((ruleset) => {
			const filteredRuleset = ruleset.filter((rs) => rs.id !== ruleSetId);

			return filteredRuleset.map((rs, idx) => ({
				...rs,
				order: idx + 1
			}));
		});
	};

	const handleDeleteRuleSet = (ruleSetId: string) => {
		updateState({ deleteModal: { show: true, ruleSetId } });
	};

	const confirmDeleteRuleSet = () => {
		if (state.deleteModal.ruleSetId) {
			removeRuleSet(state.deleteModal.ruleSetId);
		}
		updateState({ deleteModal: { show: false, ruleSetId: null } });
	};

	const cancelDeleteRuleSet = () => {
		updateState({ deleteModal: { show: false, ruleSetId: null } });
	};

	const addCondition = (ruleSetId: string) => {
		const newCondition: Condition = {
			id: generateTempId(),
			operand: "event.type",
			operator: "equals",
			value: ""
		};
		updateRuleset((ruleset) =>
			ruleset.map((rs) =>
				rs.id === ruleSetId
					? { ...rs, conditions: [...rs.conditions, newCondition] }
					: rs
			)
		);
	};

	const updateCondition = (
		ruleSetId: string,
		index: number,
		field: keyof Condition,
		value: string
	) => {
		updateRuleset((ruleset) =>
			ruleset.map((rs) =>
				rs.id === ruleSetId
					? {
							...rs,
							conditions: rs.conditions.map((condition, i) =>
								i === index ? { ...condition, [field]: value } : condition
							)
						}
					: rs
			)
		);
	};

	const removeCondition = (ruleSetId: string, index: number) => {
		updateRuleset((ruleset) =>
			ruleset.map((rs) =>
				rs.id === ruleSetId
					? { ...rs, conditions: rs.conditions.filter((_, i) => i !== index) }
					: rs
			)
		);
	};

	const addAction = (ruleSetId: string) => {
		const newAction: Action = { flowId: "" };
		updateRuleset((ruleset) =>
			ruleset.map((rs) =>
				rs.id === ruleSetId
					? { ...rs, actions: [...rs.actions, newAction] }
					: rs
			)
		);
	};

	const updateAction = (ruleSetId: string, index: number, flowId: string) => {
		updateRuleset((ruleset) =>
			ruleset.map((rs) =>
				rs.id === ruleSetId
					? {
							...rs,
							actions: rs.actions.map((action, i) =>
								i === index ? { ...action, flowId } : action
							)
						}
					: rs
			)
		);
	};

	const removeAction = (ruleSetId: string, index: number) => {
		updateRuleset((ruleset) =>
			ruleset.map((rs) =>
				rs.id === ruleSetId
					? { ...rs, actions: rs.actions.filter((_, i) => i !== index) }
					: rs
			)
		);
	};

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates
		})
	);

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;

		if (!over || active.id === over.id) {
			return;
		}

		updateRuleset((ruleset) => {
			const oldIndex = ruleset.findIndex((rs) => rs.id === active.id);

			const newIndex = ruleset.findIndex((rs) => rs.id === over.id);

			const newRuleset = arrayMove(ruleset, oldIndex, newIndex);

			return newRuleset.map((rs, idx) => ({
				...rs,
				order: idx + 1
			}));
		});
	};

	const isFormValid = () => {
		if (!isEditing) {
			if (!state.selectedFlowTypeId || !state.rule.flowActionId) {
				return false;
			}
		}

		if (state.rule.ruleset.length === 0) {
			return false;
		}

		const invalidRuleSet = state.rule.ruleset.find((rs) => {
			if (rs.conditions.length === 0) return true;

			const hasInvalidCondition = rs.conditions.some(
				(condition) =>
					!condition.operand ||
					condition.operand.trim() === "" ||
					!condition.operator ||
					condition.operator.trim() === "" ||
					condition.value === undefined ||
					condition.value === null ||
					condition.value.toString().trim() === ""
			);

			if (hasInvalidCondition) return true;

			if (rs.actions.length === 0) return true;

			const hasInvalidAction = rs.actions.some(
				(action) => !action.flowId || action.flowId.trim() === ""
			);

			if (hasInvalidAction) return true;

			return false;
		});

		if (invalidRuleSet) {
			return false;
		}

		return true;
	};

	const validateAndSubmit = () => {
		if (!isFormValid()) {
			updateState({ error: "Please fill in all required fields" });
			return;
		}

		if (!state.rule.flowActionId) {
			updateState({ error: "Please select a flow action" });
			return;
		}

		if (state.rule.ruleset.length === 0) {
			updateState({ error: "Please add at least one rule set" });
			return;
		}

		const invalidRuleSet = state.rule.ruleset.find(
			(rs) => rs.conditions.length === 0 || rs.actions.length === 0
		);
		if (invalidRuleSet) {
			updateState({
				error: "Each rule set must have at least one condition and one action"
			});
			return;
		}

		updateState({ error: null });

		try {
			const preparedRule = {
				...state.rule,
				flowTypeId: state.selectedFlowTypeId,
				ruleset: state.rule.ruleset.map((rs) => ({
					...rs,
					id: rs.id && rs.id !== "" ? rs.id : undefined,
					conditions: rs.conditions.map((c) => ({
						...c,
						id: c.id && c.id !== "" ? c.id : undefined
					})),
					actions: rs.actions.map((a) => ({
						...a,
						id: a.id && a.id !== "" ? a.id : undefined
					}))
				}))
			};

			onSubmit(preparedRule as IRule);
		} catch (_err) {
			updateState({ error: "Failed to process rule data" });
		}
	};

	const renderEmptyState = (message: string, subMessage: string) => (
		<div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center text-gray-500">
			<p>{message}</p>
			<p className="text-sm">{subMessage}</p>
		</div>
	);

	const renderCondition = (
		ruleSet: RuleSet,
		condition: Condition,
		index: number
	) => {
		const operators = getOperatorsForOperand(condition.operand);

		return (
			<div
				key={condition.id || index}
				className="flex items-center gap-3 mb-3 p-3 bg-blue-50 rounded-lg"
			>
				<select
					className="border border-gray-300 rounded px-3 py-2 bg-white"
					value={condition.operand}
					onChange={(e) =>
						updateCondition(ruleSet.id, index, "operand", e.target.value)
					}
				>
					{operands.map((op) => (
						<option key={op.value} value={op.value}>
							{op.label}
						</option>
					))}
				</select>
				<select
					className="border border-gray-300 rounded px-3 py-2 bg-white"
					value={condition.operator}
					onChange={(e) =>
						updateCondition(ruleSet.id, index, "operator", e.target.value)
					}
				>
					{operators.map((op) => (
						<option key={op.value} value={op.value}>
							{op.label}
						</option>
					))}
				</select>
				<input
					type="text"
					placeholder="Enter value"
					className="flex-1 border border-gray-300 rounded px-3 py-2"
					value={condition.value}
					onChange={(e) =>
						updateCondition(ruleSet.id, index, "value", e.target.value)
					}
				/>
				<button
					type="button"
					onClick={() => removeCondition(ruleSet.id, index)}
					className="text-red-500 hover:text-red-700"
				>
					<FontAwesomeIcon icon={faTimes} />
				</button>
			</div>
		);
	};

	const renderAction = (ruleSet: RuleSet, action: Action, index: number) => {
		const isDropdownDisabled = isEditing
			? false
			: !state.selectedFlowTypeId ||
				!state.selectedFlowActionId ||
				flowActionsQuery.isLoading;

		const availableFlows = isEditing
			? state.flows[state.rule.flowActionId || ""]
			: state.selectedFlowActionId
				? state.flows[state.selectedFlowActionId]
				: [];

		return (
			<div
				key={action.id || index}
				className="flex items-center gap-3 mb-3 p-3 bg-green-50 rounded-lg"
			>
				<select
					name="flowId"
					value={action.flowId}
					onChange={(e) => updateAction(ruleSet.id, index, e.target.value)}
					className="flex-1 border border-gray-300 rounded px-3 py-2 bg-white"
					disabled={isDropdownDisabled}
				>
					<option value="">Select a service provider</option>
					{isEditing ? (
						availableFlows?.length > 0 ? (
							availableFlows.map(
								(flow: { id: string; name: string; targetName: string }) => (
									<option key={flow.id} value={flow.id}>
										{flow.targetName}
									</option>
								)
							)
						) : (
							<option disabled>No flows available for this action</option>
						)
					) : !state.selectedFlowActionId ? (
						<option disabled>Select a flow action first</option>
					) : !state.selectedFlowTypeName ? (
						<option disabled>Select a flow type first</option>
					) : flowActionsQuery.isLoading ? (
						<option disabled>Loading flows...</option>
					) : availableFlows?.length > 0 ? (
						availableFlows.map(
							(flow: { id: string; name: string; targetName: string }) => (
								<option key={flow.id} value={flow.id}>
									{flow.targetName}
								</option>
							)
						)
					) : (
						<option disabled>No flows available for this action</option>
					)}
				</select>
				<button
					type="button"
					onClick={() => removeAction(ruleSet.id, index)}
					className="text-red-500 hover:text-red-700"
				>
					<FontAwesomeIcon icon={faTimes} />
				</button>
			</div>
		);
	};

	return (
		<>
			<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
				<div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
					<div className="flex items-center justify-between p-6 border-b">
						<h2 className="text-xl font-semibold">
							{isEditing ? "Edit Rule" : "Create Rule"}
						</h2>
						<button
							type="button"
							onClick={onClose}
							className="text-gray-400 hover:text-gray-600"
						>
							<FontAwesomeIcon icon={faTimes} />
						</button>
					</div>

					{state.error && (
						<div className="m-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
							{state.error}
						</div>
					)}

					<div className="p-6 space-y-6">
						{/* Flow Type and Flow Action selection in a card */}
						<div className="bg-gray-50 rounded-lg p-5 border border-gray-200 shadow-sm">
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								{/* Flow Type Selection */}
								<div>
									<label
										htmlFor="flowType"
										className="flex items-center text-sm font-semibold text-gray-800 mb-2"
									>
										<FontAwesomeIcon
											icon={faSitemap}
											className="text-blue-500 mr-2"
										/>
										Integration <span className="text-red-500 ml-1">*</span>
									</label>
									<div className="relative">
										<select
											className="w-full appearance-none border border-gray-300 rounded-lg px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all hover:border-blue-400 shadow-sm text-gray-700 cursor-pointer font-medium"
											value={state.selectedFlowTypeId}
											onChange={(e) =>
												updateState({ selectedFlowTypeId: e.target.value })
											}
											disabled={flowTypesQuery.isLoading || isEditing}
										>
											{/* Show selected name in edit mode */}
											{isEditing && state.selectedFlowTypeName ? (
												<option disabled className="py-2">
													{state.selectedFlowTypeName}
												</option>
											) : (
												<option value="" className="py-2">
													Select an integration
												</option>
											)}

											{/* Show loading state */}
											{!isEditing && flowTypesQuery.isLoading && (
												<option disabled className="py-2">
													Loading integrations...
												</option>
											)}

											{/* Show available integrations */}
											{!isEditing &&
												!flowTypesQuery.isLoading &&
												flowTypesQuery.data?.length > 0 &&
												flowTypesQuery.data.map((type: FlowType) => (
													<option
														key={type.id}
														value={type.id}
														className="py-2"
													>
														{type.name}
													</option>
												))}

											{/* Show fallback when no data */}
											{!isEditing &&
												!flowTypesQuery.isLoading &&
												flowTypesQuery.data?.length === 0 && (
													<option disabled className="py-2">
														No integrations available
													</option>
												)}
										</select>

										<div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
											<FontAwesomeIcon
												icon={faAngleDown}
												className="text-blue-500"
											/>
										</div>
										<style>{`
											select {
												-webkit-appearance: none;
												-moz-appearance: none;
												appearance: none;
											}
											select:focus {
												box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
											}
											/* For Firefox */
											select:-moz-focusring {
												color: transparent;
												text-shadow: 0 0 0 #000;
											}
										`}</style>
									</div>
									{isEditing && (
										<p className="mt-1 text-xs text-gray-500 italic">
											Integration cannot be changed in edit mode
										</p>
									)}
								</div>

								{/* Flow Action */}
								<div>
									<label
										htmlFor="flowAction"
										className="flex items-center text-sm font-semibold text-gray-800 mb-2"
									>
										<FontAwesomeIcon
											icon={faCheckCircle}
											className="text-blue-500 mr-2"
										/>
										Action <span className="text-red-500 ml-1">*</span>
									</label>
									<div className="relative">
										<select
											className="w-full appearance-none border border-gray-300 rounded-lg px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all hover:border-blue-400 shadow-sm text-gray-700 cursor-pointer font-medium"
											value={state.selectedFlowActionId}
											onChange={(e) =>
												updateState({ selectedFlowActionId: e.target.value })
											}
											disabled={
												!state.selectedFlowTypeId ||
												flowActionsQuery.isLoading ||
												isEditing
											}
										>
											{/* Show selected name in edit mode as disabled */}
											{isEditing && state.selectedFlowActionName ? (
												<option disabled className="py-2">
													{state.selectedFlowActionName}
												</option>
											) : (
												<option value="" className="py-2">
													Select an action
												</option>
											)}

											{/* Show when integration is not selected */}
											{!isEditing && !state.selectedFlowTypeId && (
												<option disabled className="py-2">
													Select an integration first
												</option>
											)}

											{/* Show loading state */}
											{!isEditing && flowActionsQuery.isLoading && (
												<option disabled className="py-2">
													Loading actions...
												</option>
											)}

											{/* Show list of flow actions */}
											{!isEditing &&
												state.selectedFlowTypeId &&
												!flowActionsQuery.isLoading &&
												flowActionsQuery.data?.length > 0 &&
												flowActionsQuery.data.map((action: FlowAction) => (
													<option
														key={action.id}
														value={action.id}
														className="py-2"
													>
														{action.name}
													</option>
												))}

											{/* Show empty fallback */}
											{!isEditing &&
												state.selectedFlowTypeId &&
												!flowActionsQuery.isLoading &&
												flowActionsQuery.data?.length === 0 && (
													<option disabled className="py-2">
														No actions available
													</option>
												)}
										</select>

										<div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
											<FontAwesomeIcon
												icon={faAngleDown}
												className="text-blue-500"
											/>
										</div>
									</div>
									{isEditing && (
										<p className="mt-1 text-xs text-gray-500 italic">
											Action cannot be changed in edit mode
										</p>
									)}
								</div>
							</div>
						</div>

						<div>
							<div className="flex items-center justify-between mb-4">
								<h3 className="text-lg font-medium">
									Rule Sets ({state.rule.ruleset.length})
								</h3>
								<button
									type="button"
									onClick={addRuleSet}
									disabled={!state.selectedFlowActionId && !isEditing}
									className={`bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2 ${
										!state.selectedFlowActionId && !isEditing
											? "opacity-50 cursor-not-allowed"
											: ""
									}`}
								>
									<FontAwesomeIcon icon={faPlus} />
									Add Rule Set
								</button>
							</div>

							<DndContext
								sensors={sensors}
								collisionDetection={closestCenter}
								onDragEnd={handleDragEnd}
							>
								<SortableContext
									items={state.rule.ruleset.map((rs) => rs.id)}
									strategy={verticalListSortingStrategy}
								>
									{state.rule.ruleset.map((ruleSet, index) => (
										<SortableRuleSet
											key={ruleSet.id}
											ruleSet={ruleSet}
											index={index}
											totalRulesets={state.rule.ruleset.length}
											addCondition={addCondition}
											addAction={addAction}
											handleDeleteRuleSet={handleDeleteRuleSet}
											renderEmptyState={renderEmptyState}
											renderCondition={renderCondition}
											renderAction={renderAction}
										/>
									))}
								</SortableContext>
							</DndContext>
						</div>
					</div>

					<div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
						<button
							type="button"
							onClick={onClose}
							className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
						>
							Cancel
						</button>
						<button
							type="button"
							onClick={validateAndSubmit}
							disabled={!isFormValid()}
							className={`px-4 py-2 rounded-md flex items-center gap-2 ${
								isFormValid()
									? "bg-blue-600 text-white hover:bg-blue-700"
									: "bg-blue-300 text-white cursor-not-allowed"
							}`}
						>
							{isEditing ? "Update Rule" : "Create Rule"}
						</button>
					</div>
				</div>
			</div>

			{/* Delete Rule Set Confirmation Modal */}
			{state.deleteModal.show && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
					<div className="bg-white rounded-lg p-6 max-w-md w-full">
						<div className="text-center mb-4">
							<div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
								<FontAwesomeIcon
									icon={faExclamationTriangle}
									className="h-6 w-6 text-red-600"
								/>
							</div>
							<h3 className="text-lg font-medium text-gray-900">
								Confirm Deletion
							</h3>
							<p className="text-sm text-gray-500 mt-2">
								Are you sure you want to delete this rule set? This action
								cannot be undone.
							</p>
						</div>
						<div className="flex justify-end gap-3">
							<button
								type="button"
								onClick={cancelDeleteRuleSet}
								className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
							>
								Cancel
							</button>
							<button
								type="button"
								onClick={confirmDeleteRuleSet}
								className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700"
							>
								Delete
							</button>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default AddRuleForm;
